import { useState } from 'react'
import { LegalControlConsentPanel } from './LegalControlConsentPanel'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { read, command } from '@/services/vault/vault.http'
import type { ClientTokens } from '@/services/vault/auth'
import { Field, useCommand, CommandStatus, TERMS, RETENTION } from './VaultForms'
import AccountReference from './AccountReference'

export function ClaimVaultPage() {
  const { adoptTokens } = useAuth(),
    actions = useCommand()
  const [type, setType] = useState('individual'),
    [source, setSource] = useState(''),
    [code, setCode] = useState(''),
    [ack, setAck] = useState(false)
  return (
    <section className="card card-elevated p-6 space-y-4 text-text-primary max-w-2xl">
      <h1 className="text-2xl font-semibold">Claim my independent client vault</h1>
      <p className="text-text-secondary">
        Use your existing verified client account to claim an individual record. Business and HUF
        control require governed legal review. A name,email or PAN match does not establish ownership.
      </p>
      <p className="text-sm text-text-secondary">
        Your identity, password and MFA remain yours. Tenant sessions are revoked after claiming;
        future sign-in uses Client vault on the login page. {RETENTION}
      </p>
      <form
        className="space-y-4"
        onSubmit={event => {
          event.preventDefault()
          const payload = { customer_type: type, customer_id: Number(source), terms_version: TERMS }
          void actions.run(async () => {
            if (!adoptTokens) throw new Error('Session adoption unavailable')
            const data = await command<ClientTokens>('/auth/client/claim', {
              ...payload,
              mfa_code: code.trim(),
              idempotency_key: actions.idempotencyKey(payload),
            })
            await adoptTokens(data)
            window.location.assign('/client-vault')
          })
        }}
      >
        <label className="block text-sm text-text-secondary">
          Customer record type
          <select
            className="input input-bordered w-full"
            value={type}
            onChange={event => {
              setType(event.target.value)
              setAck(false)
            }}
          >
            <option value="individual">Individual</option>
            <option value="business" disabled>Business: governed review required</option>
          </select>
        </label>
        <Field
          label="Your exact legacy customer record number"
          type="number"
          min={1}
          value={source}
          required
          onChange={event => {
            setSource(event.target.value)
            setAck(false)
          }}
        />
        <Field
          label="Authenticator or backup code"
          value={code}
          required
          autoComplete="one-time-code"
          onChange={event => setCode(event.target.value)}
        />
        <p className="text-sm text-text-secondary">Terms: {TERMS}</p>
        <label className="flex gap-2 text-sm">
          <input
            type="checkbox"
            required
            checked={ack}
            onChange={event => setAck(event.target.checked)}
          />
          I own this client record and consent to claiming an independent vault.
        </label>
        <button className="btn btn-primary" disabled={actions.busy || !ack || type!=='individual'}>
          Claim my vault
        </button>
        <CommandStatus {...actions} />
      </form>
    </section>
  )
}
export function VaultAccountPage() {
  const { user, logout } = useAuth()
  const sessions = useQuery({
    queryKey: ['vault', user?.id, user?.authScope, 'sessions'],
    queryFn: () =>
      read<Array<{ session_id: string; created_at: string; expires_at: string }>>(
        '/auth/client/sessions'
      ),
    staleTime: 0,
  })
  return (
    <section className="card card-elevated p-6 space-y-4 text-text-primary">
      <Link to="/client-vault" className="text-primary-600 dark:text-primary-400">
        Back to vault
      </Link>
      <h1 className="text-2xl font-semibold">Independent account sessions</h1>
      <AccountReference />
      <LegalControlConsentPanel />
      <p className="text-text-secondary">
        Current sessions are checked against the live account. Sign out ends this session and its
        refresh token.
      </p>
      {sessions.error && <p role="alert">{sessions.error.message}</p>}
      {sessions.data?.map(row => (
        <div className="border border-surface-overlay rounded-lg p-4" key={row.session_id}>
          <p>
            {row.session_id === user?.currentSessionId ? 'This session' : 'Other active session'}
          </p>
          <p className="text-sm text-text-secondary">
            Created {row.created_at} · Expires {row.expires_at}
          </p>
        </div>
      ))}
      <button className="btn btn-primary" onClick={() => void logout()}>
        Sign out this session
      </button>
    </section>
  )
}
